// Desktop navigation uses ordinary links and sibling disclosure buttons.
class DropdownMenu extends HTMLElement {
  connectedCallback() {
    if (this.initialized) {
      document.addEventListener('click', this.onOutsideClick);
      document.addEventListener('keydown', this.onOutsideEscape);
      return;
    }
    this.initialized = true;
    this.header = this.closest('.shopify-section-header');
    this.trigger = this.querySelector(':scope > .desktop-menu-toggle');
    this.panel = this.querySelector(':scope > .list-menu-dropdown');
    this.timeouts = [];

    this.trigger?.addEventListener('click', () => this.setOpen(!this.isOpen));
    this.addEventListener('mouseenter', () => this.setOpen(true));
    this.addEventListener('mouseleave', () => {
      if (!this.contains(document.activeElement)) this.close();
    });
    this.addEventListener('focusout', () => {
      // Wait for the browser to move focus, including Shift+Tab to the parent.
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 0);
    });
    this.addEventListener('keydown', (event) => {
      // Enter on a menu destination also opens its closed disclosure.
      // Once open, preserve the link's normal navigation and modified shortcuts.
      if (event.key === 'Enter' && !event.defaultPrevented &&
          !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey &&
          this.panel && !this.isOpen &&
          event.target === this.querySelector(':scope > a')) {
        event.preventDefault();
        this.setOpen(true);
        return;
      }
      if (event.key !== 'Escape' || !this.isOpen) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
      this.trigger.focus();
    });
    this.onOutsideClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    this.onOutsideEscape = (event) => {
      if (event.key === 'Escape' && !this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onOutsideClick);
    document.addEventListener('keydown', this.onOutsideEscape);
    this.reset();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutsideClick);
    document.removeEventListener('keydown', this.onOutsideEscape);
    this.close();
  }

  get isOpen() {
    return this.classList.contains('is-active');
  }

  setOpen(open) {
    if (!this.header) return;
    if (!open) {
      this.close();
      return;
    }
    // Keep ancestors open for nested dropdowns; close other branches.
    this.header.querySelectorAll('dropdown-menu, mega-menu').forEach((menu) => {
      if (menu !== this && !menu.contains(this) && !this.contains(menu)) menu.reset?.();
    });
    if (!this.panel || this.isOpen) {
      this.updateBackground();
      return;
    }
    this.panel.classList.remove('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('is-active');
    const children = this.animationChildren;
    const delay = children.length > 5 ? 75 : 150;
    children.forEach((element, index) => {
      this.timeouts.push(setTimeout(() => element.classList.add('show-from-bottom'), delay * index + 100));
    });
    this.updateBackground();
  }

  get animationChildren() {
    return this.querySelectorAll(':scope > .list-menu-dropdown > .list-menu');
  }

  reset() {
    this.querySelectorAll('dropdown-menu, mega-menu').forEach((menu) => menu.reset?.());
    this.timeouts?.forEach(clearTimeout);
    this.timeouts = [];
    this.classList.remove('is-active');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.panel?.classList.add('hidden');
    this.animationChildren.forEach((element) => element.classList.remove('show-from-bottom'));
  }

  close() {
    this.reset();
    this.updateBackground();
  }

  updateBackground() {
    if (!this.header) return;
    const active = this.header.querySelector('dropdown-menu.is-active, mega-menu.is-active');
    this.header.classList.toggle('is-hover', Boolean(active));
    const background = this.header.querySelector('.header-background');
    if (!background) return;
    const mega = this.header.querySelector('mega-menu.is-active');
    background.classList.toggle('is-active', Boolean(mega));
    if (mega) background.style.setProperty('--height', this.header.clientHeight + mega.panel.clientHeight + 'px');
  }
}
customElements.define('dropdown-menu', DropdownMenu);

class MegaMenu extends DropdownMenu {
  get animationChildren() {
    return this.querySelectorAll('.mega-menu__item, .mega-menu__promo');
  }
}
customElements.define('mega-menu', MegaMenu);
